import { ArrowDropUp, ArrowDropDown, Check } from '@mui/icons-material';
import { ListItemIcon, ListItemText, MenuItem, Menu } from '@mui/material';
import { MenuButton } from 'assets/style-components/button';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useStore } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { registerMenuState, updateMenuState } from 'slices/customViewSlice';

const defaultGroupByOptions = {
  None: {
    name: 'None',
  },
  // lbs: {
  //   name: 'lbs',
  //   key: '$lbs',
  //   groups: (gantt) => {
  //     const lbsData = gantt?.constants?.lbsData;
  //     const groups = [
  //       ...Object.values(lbsData.areas).map((area) => ({
  //         key: `area-${area.id}`,
  //         $lbs: `zone-${area.zone_id}`,
  //         label: area.name,
  //       })),
  //       ...Object.values(lbsData.zones).map((zone) => ({
  //         key: `zone-${zone.id}`,
  //         $lbs: `location-${zone.location_id}`,
  //         label: zone.name,
  //       })),
  //       ...Object.values(lbsData.locations).map((location) => ({
  //         key: `location-${location.id}`,
  //         label: location.name,
  //       })),
  //     ];
  //     return groups;
  //   },
  //   onBeforeGroup: (gantt) => {
  //     gantt.eachTask((task) => {
  //       task.$lbs = task?.area_id
  //         ? `area-${task.area_id}`
  //         : task?.zone_area
  //           ? `zone-${task.zone_area}`
  //           : task?.location_id
  //             ? `location-${task.area_id}`
  //             : undefined;
  //     });
  //   },
  // },
  Area: {
    name: 'Area',
    key: 'area_id',
    groups: (state) => {
      const locations = state.locations.entities;
      const zones = state.zones.entities;
      const areas = state.areas.entities;
      if (isEmpty(areas)) {
        return [];
      }
      const groups = Object.values(areas).map((area) => {
        const zone = zones[area.zone_id];
        const location = locations[zone.location_id];
        return {
          key: area.id,
          label: `${location?.name} - ${zone?.name} - ${area?.name}`,
        };
      });
      return groups;
    },
    defaultGroupLabel: 'No Area Assigned',
  },
  Zone: {
    name: 'Zone',
    key: 'zone_id',
    groups: (state) => {
      const zones = state.zones.entities;
      const locations = state.locations.entities;
      if (isEmpty(zones)) {
        return [];
      }
      const groups = Object.values(zones).map((zone) => {
        const location = locations[zone.location_id];
        return {
          key: zone.id,
          label: `${location?.name} - ${zone?.name}`,
        };
      });
      return groups;
    },
    defaultGroupLabel: 'No Zone Assigned',
  },
  Location: {
    name: 'Location',
    key: 'location_id',
    groups: (state) => {
      const locations = state.locations.entities;
      if (isEmpty(locations)) {
        return [];
      }
      const groups = Object.values(locations).map((location) => {
        return {
          key: location.id,
          label: `${location?.name}`,
        };
      });
      return groups;
    },
    defaultGroupLabel: 'No Location Assigned',
  },
};

/**
 * Group by component
 * @param {string} ganttId
 * @param {[object]} options
 * @returns
 */
const GroupBy = ({ gantt, options = defaultGroupByOptions }) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const selectedGroupBy = useSelector((state) => state.customViews?.menus?.groupBy?.currentValue);
  const state = useStore().getState();

  const handleSelectOption = (event, option) => {
    dispatch(updateMenuState({ name: 'groupBy', value: option.name }));
  };

  useEffect(() => {
    if (selectedGroupBy && gantt?.hasOwnProperty('groupBy')) {
      if (selectedGroupBy === 'None') {
        gantt?.groupBy(false);
      } else {
        const optionObj = options[selectedGroupBy];
        gantt.attachEvent(
          'onBeforeTaskDisplay',
          function (id, task) {
            if (task.type === 'parent_task' || task.type === 'project_bar') {
              return false;
            }
            if (task.$virtual) {
              task.type = 'project';
            }
            return true;
          },
          { id: 'groupByFilter' }
        );
        gantt?.groupBy({
          relation_property: optionObj.key,
          groups: [
            ...(typeof optionObj.groups === 'function'
              ? optionObj.groups(state)
              : optionObj.groups),
            { key: null, label: 'None' },
          ],
          group_id: 'key',
          group_text: 'label',
          default_group_label: optionObj.defaultGroupLabel || 'None',
          save_tree_structure: false,
        });
        gantt.message({
          type: 'info',
          text: 'Parent tasks are hidden when a group by is selected',
        });
        // gantt?.sort('start_date');
      }
    }
    return () => {
      gantt?.detachEvent('groupByFilter');
    };
  }, [selectedGroupBy, gantt, options, state]);

  useEffect(() => {
    dispatch(registerMenuState({ name: 'groupBy', value: 'None' }));
  }, [dispatch]);

  return (
    <>
      <MenuButton
        isHighlighted={selectedGroupBy !== 'None'}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        Group By{Boolean(anchorEl) ? <ArrowDropUp /> : <ArrowDropDown />}
      </MenuButton>
      {anchorEl !== null ? (
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          getContentAnchorEl={null}
          open={Boolean(anchorEl)}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          onClose={() => setAnchorEl(null)}
        >
          {Object.values(options)?.map((option) => {
            const selected = option?.name === selectedGroupBy;
            return (
              <MenuItem key={option.name} onClick={(event) => handleSelectOption(event, option)}>
                <ListItemIcon>
                  {selected ? <Check color={'action'} style={{ display: 'block' }} /> : undefined}
                </ListItemIcon>
                <ListItemText>{option?.name}</ListItemText>
              </MenuItem>
            );
          })}
        </Menu>
      ) : null}
    </>
  );
};

export default GroupBy;
